import { mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'

import Popover from 'src/components/popover/popover.vue'
import ActionButtonContainer from './action_button_container.vue'
import { BUTTONS } from './buttons_definitions.js'

import { useSyncConfigStore } from 'src/stores/sync_config.js'

import genRandomSeed from 'src/services/random_seed/random_seed.service.js'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons'

library.add(faEllipsisH)

const StatusActionButtons = {
  props: ['status', 'replying'],
  emits: ['toggleReplying', 'onSuccess', 'onError'],
  data() {
    return {
      showPin: false,
      showingConfirmDialog: false,
      currentConfirmTitle: '',
      currentConfirmOkText: '',
      currentConfirmCancelText: '',
      currentConfirmAction: () => {
        /* no-op */
      },
      randomSeed: genRandomSeed(),
      emojiPickerShown: false,
    }
  },
  components: {
    Popover,
    ConfirmModal: defineAsyncComponent(
      () => import('src/components/confirm_modal/confirm_modal.vue'),
    ),

    ActionButtonContainer,
  },
  computed: {
    ...mapState(useSyncConfigStore, {
      pinnedItems: (store) =>
        new Set(store.prefsStorage.collections.pinnedStatusActions),
    }),
    buttons() {
      return BUTTONS.filter((x) => (x.if ? x.if(this.funcArg) : true))
    },
    quickButtons() {
      return this.buttons.filter((x) => this.pinnedItems.has(x.name))
    },
    extraButtons() {
      return this.buttons.filter((x) => !this.pinnedItems.has(x.name))
    },
    currentUser() {
      return this.$store.state.users.currentUser
    },
    funcArg() {
      return {
        status: this.status,
        replying: this.replying,
        emojiPickerShown: this.emojiPickerShown,
        emit: this.$emit,
        dispatch: this.$store.dispatch,
        state: this.$store.state,
        getters: this.$store.getters,
        router: this.$router,
        currentUser: this.currentUser,
        loggedIn: !!this.currentUser,
      }
    },
    triggerAttrs() {
      return {
        title: this.$t('status.more_actions'),
        'aria-controls': `popup-menu-${this.randomSeed}`,
        'aria-haspopup': 'menu',
      }
    },
  },
  methods: {
    doAction(button) {
      if (button.confirm?.(this.funcArg)) {
        // TODO move to action_button
        this.currentConfirmTitle = this.$t(
          button.confirmStrings(this.funcArg).title,
        )
        this.currentConfirmOkText = this.$t(
          button.confirmStrings(this.funcArg).confirm,
        )
        this.currentConfirmCancelText = this.$t(
          button.confirmStrings(this.funcArg).cancel,
        )
        this.currentConfirmBody = this.$t(
          button.confirmStrings(this.funcArg).body,
        )
        this.currentConfirmAction = () => {
          this.showingConfirmDialog = false
          this.doActionReal(button)
        }
        this.showingConfirmDialog = true
      } else {
        this.doActionReal(button)
      }
    },
    doActionReal(button) {
      const promise = button.action?.(this.funcArg) ?? Promise.resolve()
      promise
        .then(() => this.$emit('onSuccess'))
        .catch((err) => this.$emit('onError', err))
    },
    onExtraClose() {
      this.showPin = false
    },
    onEmojiPickerShown(state) {
      this.emojiPickerShown = state
    },
    isPinned(button) {
      return this.pinnedItems.has(button.name)
    },
    unpin(button) {
      useSyncConfigStore().removeCollectionPreference({
        path: 'collections.pinnedStatusActions',
        value: button.name,
      })
      useSyncConfigStore().pushSyncConfig()
    },
    pin(button) {
      useSyncConfigStore().addCollectionPreference({
        path: 'collections.pinnedStatusActions',
        value: button.name,
      })
      useSyncConfigStore().pushSyncConfig()
    },
    getComponent(button) {
      if (!this.$store.state.users.currentUser && button.anonLink) {
        return 'a'
      } else if (button.action == null && button.link != null) {
        return 'a'
      } else {
        return 'button'
      }
    },
    getClass(button) {
      return {
        [button.name + '-button']: true,
        disabled: button.interactive
          ? !button.interactive(this.funcArg)
          : false,
        '-pin-edit': this.showPin,
        '-dropdown': button.dropdown?.(),
        '-active': button.active?.(this.funcArg),
      }
    },
  },
}

export default StatusActionButtons
